import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Loader2, Search, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "business", label: "Business" },
  { value: "capital", label: "Capital" },
];

const initials = (s) => String(s || "?").trim().slice(0, 2).toUpperCase();

export const orgHref = (org) => {
  if (!org?.slug) return null;
  return org.profile_type === "capital" ? `/capital/${org.slug}` : `/b/${org.slug}`;
};

/**
 * The canonical organization identity presentation — avatar, display name, link to the
 * real profile. The same trio Business and Capital use everywhere else; affiliations do
 * not get a bespoke organization card.
 */
export function OrgIdentity({ org, testId }) {
  const href = orgHref(org);
  const name = org?.display_name || "Unnamed organization";
  return (
    <div className="flex min-w-0 items-center gap-2" data-testid={testId}>
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={org?.image_url || undefined} alt="" />
        <AvatarFallback>{initials(name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        {href ? (
          <Link to={href} className="block truncate text-sm font-medium hover:text-teal-700">
            {name}
          </Link>
        ) : <p className="truncate text-sm font-medium">{name}</p>}
        <p className="text-xs capitalize text-muted-foreground">
          {org?.profile_type === "capital" ? "Capital" : "Business"}
        </p>
      </div>
    </div>
  );
}

function Section({ title, hint, children, testId }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * Groups Batch C.1 — organizational affiliations (GRP-DEC-280).
 *
 * TWO-SIDED consent, surfaced honestly: asking is not affiliating. The Group may ask ANY
 * published Business or Capital profile, including one nobody here controls — and that
 * organization's own Owner/Admin is the only party who can approve it. Approve / Decline
 * is therefore offered per row, from a server-computed `can_decide`, never from "we asked,
 * so we must be allowed".
 */
export function GroupAffiliations({ groupId }) {
  const qc = useQueryClient();
  const [ptype, setPtype] = React.useState("business");
  const [term, setTerm] = React.useState("");
  const [q, setQ] = React.useState("");
  const [busyId, setBusyId] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setQ(term.trim()), 350);
    return () => clearTimeout(t);
  }, [term]);

  const list = useQuery({
    queryKey: ["group-affiliations", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/affiliations`)).data,
  });
  const rows = list.data?.affiliations || [];
  const canManage = Boolean(list.data?.can_manage);

  const search = useQuery({
    queryKey: ["group-affiliation-search", groupId, ptype, q],
    queryFn: async () => (await api.get(`/groups/${groupId}/affiliations/search`,
      { params: { profile_type: ptype, ...(q ? { q } : {}) } })).data,
    enabled: canManage,
  });
  const results = search.data?.results || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["group-affiliations", groupId] });
    qc.invalidateQueries({ queryKey: ["group-affiliation-search", groupId] });
  };

  const act = async (id, fn, msg) => {
    setBusyId(id);
    try { await fn(); toast.success(msg); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work."); }
    finally { setBusyId(null); }
  };

  const request = (org) => act(org.id,
    () => api.post(`/groups/${groupId}/affiliations`,
      { profile_type: org.profile_type, profile_id: org.id }),
    "Request sent — the organization decides.");

  const approved = rows.filter((r) => r.state === "approved");
  const pending = rows.filter((r) => r.state === "pending");
  const declined = rows.filter((r) => r.state === "declined");

  if (list.isLoading) {
    return <Card className="border-0 p-6" data-testid="group-affiliations">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    </Card>;
  }

  return (
    <div className="space-y-4" data-testid="group-affiliations">
      <Card className="space-y-4 border-0 p-4 sm:p-6">
        <div>
          <h2 className="text-base font-semibold">Affiliated organizations</h2>
          <p className="text-sm text-muted-foreground">
            An affiliation needs both sides to agree. You can ask any published business or
            capital profile — only its owner or admins can accept, and only accepted
            affiliations appear on your group page.
          </p>
        </div>

        {canManage ? (
          <div className="space-y-2" data-testid="group-affiliation-request">
            <Label>Request an affiliation</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={ptype} onValueChange={(v) => { setPtype(v); setTerm(""); }}>
                <SelectTrigger className="w-full sm:w-40"
                                data-testid="group-affiliation-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}
                                data-testid={`group-affiliation-type-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={term} onChange={(e) => setTerm(e.target.value)}
                       className="pl-8" placeholder="Search by name…"
                       data-testid="group-affiliation-search-input" />
              </div>
            </div>
            <div className="rounded-lg border border-border"
                 data-testid="group-affiliation-results">
              {search.isFetching && !results.length ? (
                <p className="px-3 py-3 text-sm text-muted-foreground">Searching…</p>
              ) : results.length ? (
                <ul className="divide-y divide-border">
                  {results.map((org) => (
                    <li key={org.id} className="flex items-center gap-2 px-3 py-2">
                      <div className="min-w-0 flex-1"><OrgIdentity org={org} /></div>
                      <Button size="sm" variant="outline" className="shrink-0"
                              disabled={busyId === org.id}
                              onClick={() => request(org)}
                              data-testid={`group-affiliation-request-${org.id}`}>
                        {busyId === org.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Request"}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-3 text-sm text-muted-foreground">
                  {q ? "No published organization matches that name."
                    : "No organizations available to ask yet."}
                </p>
              )}
            </div>
          </div>
        ) : null}
      </Card>

      <Card className="space-y-5 border-0 p-4 sm:p-6">
        <Section title="Approved" hint="These are shown publicly on the group's Details tab."
                 testId="group-affiliations-approved">
          {approved.length ? (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {approved.map((r) => (
                <li key={r.id} className="flex items-center gap-2 px-3 py-2"
                    data-testid={`group-affiliation-row-${r.id}`}>
                  <div className="min-w-0 flex-1"><OrgIdentity org={r.org} /></div>
                  <Badge variant="secondary" className="shrink-0 gap-1">
                    <Check className="h-3 w-3" aria-hidden="true" />Affiliated
                  </Badge>
                  {canManage ? (
                    <Button size="sm" variant="ghost" className="shrink-0 text-xs"
                            disabled={busyId === r.id}
                            onClick={() => act(r.id,
                              () => api.delete(`/groups/${groupId}/affiliations/${r.id}`),
                              "Affiliation removed.")}
                            data-testid={`group-affiliation-remove-${r.id}`}>
                      Remove
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No affiliations yet.</p>
          )}
        </Section>

        {canManage ? (
          <>
            <Section title="Pending"
                     hint="Waiting on whichever side did not ask."
                     testId="group-affiliations-pending">
              {pending.length ? (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {pending.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2"
                        data-testid={`group-affiliation-row-${r.id}`}>
                      <div className="min-w-0 flex-1"><OrgIdentity org={r.org} /></div>
                      <span className="shrink-0 text-xs text-muted-foreground"
                            data-testid={`group-affiliation-state-${r.id}`}>
                        {r.requested_side === "group"
                          ? "You asked — awaiting the organization"
                          : "They asked — awaiting your decision"}
                      </span>
                      {r.can_decide ? (
                        <>
                          <Button size="sm" className="shrink-0" disabled={busyId === r.id}
                                  onClick={() => act(r.id,
                                    () => api.post(`/groups/${groupId}/affiliations/${r.id}/approve`, {}),
                                    "Affiliation approved.")}
                                  data-testid={`group-affiliation-approve-${r.id}`}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="shrink-0"
                                  disabled={busyId === r.id}
                                  onClick={() => act(r.id,
                                    () => api.post(`/groups/${groupId}/affiliations/${r.id}/decline`, {}),
                                    "Request declined.")}
                                  data-testid={`group-affiliation-decline-${r.id}`}>
                            Decline
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="ghost" className="shrink-0 text-xs"
                                disabled={busyId === r.id}
                                onClick={() => act(r.id,
                                  () => api.delete(`/groups/${groupId}/affiliations/${r.id}`),
                                  "Request withdrawn.")}
                                data-testid={`group-affiliation-withdraw-${r.id}`}>
                          <X className="mr-1 h-3.5 w-3.5" />Withdraw
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nothing pending.</p>
              )}
            </Section>

            {declined.length ? (
              <Section title="Declined" hint="History only — never shown publicly."
                       testId="group-affiliations-declined">
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {declined.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2"
                        data-testid={`group-affiliation-row-${r.id}`}>
                      <div className="min-w-0 flex-1"><OrgIdentity org={r.org} /></div>
                      <Badge variant="outline" className="shrink-0">Declined</Badge>
                      <Button size="sm" variant="ghost" className="shrink-0 text-xs"
                              disabled={busyId === r.id}
                              onClick={() => act(r.id,
                                () => api.post(`/groups/${groupId}/affiliations`,
                                  { profile_type: r.profile_type, profile_id: r.profile_id }),
                                "Request sent again.")}
                              data-testid={`group-affiliation-reask-${r.id}`}>
                        Ask again
                      </Button>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}
          </>
        ) : null}
      </Card>
    </div>
  );
}
